import { type Action, createStore } from 'react-sweet-state';

import type { ReviewerProject } from 'src/data/project';
import { queryReviewerProject } from 'src/graphql/project';
import { isGraphQLAccessError } from 'src/lib/graphql';
import { captureMessage } from 'src/lib/sentry';

export const REVIEWER_PROJECT_LOAD_ERROR = {
  accessLost: 'accessLost',
  failed: 'failed',
} as const;
export type ReviewerProjectLoadError =
  (typeof REVIEWER_PROJECT_LOAD_ERROR)[keyof typeof REVIEWER_PROJECT_LOAD_ERROR];

export interface State {
  project: ReviewerProject | null;
  loading: boolean;
  error: ReviewerProjectLoadError | null;
}

export interface ContainerProps {
  projectId: string;
}

export type Actions = typeof ACTIONS;

type StoreAction<TResult = void> = Action<State, ContainerProps, TResult>;

const INITIAL_STATE: State = {
  project: null,
  loading: true,
  error: null,
};

export const PRIVATE_ACTIONS = {
  init:
    (): StoreAction =>
    ({ setState }) => {
      setState(INITIAL_STATE);
    },
};

export const ACTIONS = {
  loadProject:
    (): StoreAction<Promise<void>> =>
    async ({ setState }, { projectId }) => {
      setState({ loading: true, error: null });
      try {
        const project = await queryReviewerProject({ id: Number(projectId) });
        setState(
          project ? { project } : { project: null, error: REVIEWER_PROJECT_LOAD_ERROR.accessLost }
        );
      } catch (error) {
        if (isGraphQLAccessError(error as Error)) {
          setState({ error: REVIEWER_PROJECT_LOAD_ERROR.accessLost });
          return;
        }
        captureMessage('Failed to load a project shared for review', {
          level: 'error',
          extra: { error, projectId },
        });
        setState({ error: REVIEWER_PROJECT_LOAD_ERROR.failed });
      } finally {
        setState({ loading: false });
      }
    },
};

export const Store = createStore<State, Actions, ContainerProps>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'ReviewerProjectPage',
});
